import { nextSort, type SortState as TableSortState } from '@/components/data-table/tableSort';
import { entityLabel, fieldLabel } from './fieldLabels';
import type { RecordGroup } from './sheetTabConfigs';
import type { SyncChange } from './syncTypes';

export type Effective = 'write' | 'fix' | 'conflict' | 'gone' | 'skipped';
export type Area = 'project' | 'sync';

/** Lựa chọn tại chỗ của người dùng, chưa gửi lên máy chủ: `skip` theo change.id, `archive` theo natural_key. */
export interface LocalChoice {
  skip: ReadonlySet<string>;
  archive: ReadonlySet<string>;
}

/** `source`: nguồn của dòng chấm công đang có trong hệ thống ('machine' | 'manual' | 'sheet'), chỉ dòng chấm công cập nhật mới có (đọc từ bảng `attendance`). */
export type Row = SyncChange & { effective: Effective; archive: boolean; source?: string; /** Nguồn giờ công chưa biết: đang tải ('checking') hoặc không đọc được ('failed'). */ sourceState?: 'checking' | 'failed'; /** Hồ sơ máy chủ không cho nghỉ việc qua đồng bộ: quản trị viên / chính mình, hoặc đã lưu trữ. */ protectedWhy?: 'admin' | 'archived' };

/** Bản ghi con của dự án mà dòng "không còn trên Sheet" xoá được (khác dự án, chỉ lưu trữ). */
const DELETABLE_ENTITIES: readonly string[] = ['phase', 'milestone', 'payment'];

export type GoneAction = 'archive' | 'delete' | 'offboard';

/**
 * Việc của dòng "không còn trên Sheet", theo khu: Dự án → lưu trữ dự án / xoá giai đoạn · mốc · đợt TT; Nhân sự → cho nghỉ việc;
 * Chấm công → xoá ngày công. Không có việc (null) thì dòng luôn "Giữ nguyên".
 */
export function goneActionOf(r: { effective: Effective; entity: string }, area: Area): GoneAction | null {
  if (r.effective !== 'gone') return null;
  if (area === 'project') {
    if (r.entity === 'project') return 'archive';
    return DELETABLE_ENTITIES.includes(r.entity) ? 'delete' : null;
  }
  if (r.entity === 'profile') return 'offboard';
  return r.entity === 'attendance' ? 'delete' : null;
}

/** Trường hồ sơ quyết định quyền đăng nhập: KHÔNG ghi qua Sheet, đổi ở Cài đặt (dòng chỉ để xem, không có nút). */
const SETTINGS_ONLY_FIELDS: readonly string[] = ['role', 'email', 'active'];
export const settingsOnly = (r: { entity: string; kind: string; field: string | null }): boolean =>
  r.entity === 'profile' && r.kind === 'update' && SETTINGS_ONLY_FIELDS.includes(r.field ?? '');

/** Dòng chấm công cập nhật mà dòng hệ thống đã sửa tay trong app (`manual`): máy chủ từ chối ghi đè, nên giữ nguyên. */
export const heldByApp = (r: Row): boolean => r.entity === 'attendance' && r.kind === 'update' && r.source === 'manual';
/** Chưa biết nguồn của ngày công trong hệ thống: không cho chấp nhận, vì có thể là giờ máy bị ghi đè mà không ai thấy. */
export const sourceUnverified = (r: Row): boolean => r.entity === 'attendance' && r.kind === 'update' && r.sourceState !== undefined;
/** Dòng "sẽ ghi" không được duyệt dù đã nằm trong tập chấp nhận: chỉ xem (Cài đặt), giữ nguyên (sửa tay), hoặc chưa biết nguồn. */
export const writeBlocked = (r: Row): boolean => settingsOnly(r) || heldByApp(r) || sourceUnverified(r);

/** Chữ của việc khi dòng không còn trên Sheet, theo loại bản ghi. */
export const missingWord = (entity: string): string => (entity === 'project' ? 'Lưu trữ' : entity === 'profile' ? 'Cho nghỉ việc' : entity === 'attendance' ? 'Xoá ngày công' : 'Xoá');

/** Dòng chấm công cập nhật sẽ ghi đè giờ do máy chấm công sinh ra: vẫn chấp nhận được nhưng phải nói rõ. */
export const overwritesMachine = (r: Row): boolean => r.entity === 'attendance' && r.kind === 'update' && r.source === 'machine';

/** Dòng "sẽ ghi" mà người xem chấp nhận / bỏ qua được. Thêm nhân sự (tạo hồ sơ) chỉ quản trị. */
export const writableBy = (r: Row, isAdmin: boolean): boolean =>
  r.effective === 'write' && !writeBlocked(r) && !(r.entity === 'profile' && r.kind === 'create' && !isAdmin);

const ORDER: Effective[] = ['fix', 'conflict', 'write', 'gone', 'skipped'];

export function classify(c: SyncChange, local: LocalChoice): Effective {
  // Dòng cần sửa bỏ qua được (chỉ là lựa chọn tại chỗ): nó không bao giờ vào `apply_sync_changes` và không tính vào `remaining`.
  if (c.kind === 'invalid') return local.skip.has(c.id) ? 'skipped' : 'fix';
  if (c.decision === 'conflict') return 'conflict';
  if (c.kind === 'missing') return 'gone';
  return local.skip.has(c.id) || c.decision === 'skipped' ? 'skipped' : 'write';
}

/** Dòng đã áp dụng/lưu trữ/giữ nguyên ở lần trước không còn việc phải làm nên không vào bảng. */
export function buildRows(changes: SyncChange[], local: LocalChoice, area: Area): Row[] {
  return changes
    .filter((c) => c.decision === 'pending' || c.decision === 'skipped' || c.decision === 'conflict')
    .map((c) => {
      const effective = classify(c, local);
      // Chỉ dòng DỰ ÁN có "Lưu trữ" (theo khoá); giai đoạn/mốc/đợt TT chọn "Xoá" theo id vì khoá tự nhiên của chúng có thể trùng nhau; Nhân sự/Chấm công luôn giữ nguyên.
      const archive = effective === 'gone' && area === 'project' && c.entity === 'project' && local.archive.has(c.naturalKey);
      return { ...c, effective, archive };
    });
}

export function sortRows(rows: Row[]): Row[] {
  return [...rows].sort(
    (a, b) =>
      ORDER.indexOf(a.effective) - ORDER.indexOf(b.effective) ||
      a.naturalKey.localeCompare(b.naturalKey) ||
      (a.field ?? '').localeCompare(b.field ?? '') ||
      a.id.localeCompare(b.id),
  );
}

export function groupByRecord(rows: Row[]): { naturalKey: string; rowLabel: string; rows: Row[] }[] {
  const groups = new Map<string, { naturalKey: string; rowLabel: string; rows: Row[] }>();
  for (const r of rows) {
    const g = groups.get(r.naturalKey);
    if (g) g.rows.push(r);
    else groups.set(r.naturalKey, { naturalKey: r.naturalKey, rowLabel: r.rowLabel, rows: [r] });
  }
  return [...groups.values()];
}

export type SortKey = 'record' | 'date' | 'source' | 'action';
export type SortState = TableSortState<SortKey>;
export { nextSort };
type Group = ReturnType<typeof groupByRecord>[number];

const textOf = (v: unknown) => (v === null || v === undefined ? '' : Array.isArray(v) ? v.join('-') : String(v));

/** Sắp xếp nhóm theo cột người dùng chọn; không chọn thì giữ thứ tự mặc định (cần sửa → xung đột → sẽ ghi…). Ổn định theo khoá. */
export function sortGroups(groups: Group[], sort: SortState | null): Group[] {
  if (!sort) return groups;
  const val = (g: Group) =>
    sort.key === 'record' ? g.rowLabel
      : sort.key === 'date' ? (g.rows[0].workDate ?? '')
      : sort.key === 'source' ? textOf(g.rows[0].newValue)
        : String(Math.min(...g.rows.map((r) => ORDER.indexOf(r.effective))));
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...groups].sort((a, b) => sign * val(a).localeCompare(val(b), 'vi', { numeric: true }) || a.naturalKey.localeCompare(b.naturalKey));
}

const willWrite =(r: Row) => r.effective === 'write' || r.archive;

/** 'n sẽ ghi · m bỏ qua · k chưa xong' */
export function footerSummary(rows: Row[]): { write: number; skipped: number; unfinished: number } {
  return {
    write: rows.filter(willWrite).length,
    skipped: rows.filter((r) => r.effective === 'skipped').length,
    unfinished: rows.filter((r) => r.effective === 'fix' || r.effective === 'conflict').length,
  };
}

export function confirmSummary(
  rows: Row[],
  area: Area,
): { write: number; skipped: number; fix: number; conflict: number; gone: number; consequence: string } {
  const f = footerSummary(rows);
  return {
    write: f.write,
    skipped: f.skipped,
    fix: rows.filter((r) => r.effective === 'fix').length,
    conflict: rows.filter((r) => r.effective === 'conflict').length,
    gone: rows.filter((r) => r.effective === 'gone' && !r.archive).length,
    consequence:
      area === 'project'
        ? 'Dự án sẽ được cập nhật theo Sheet. Dòng không còn trên Sheet chỉ lưu trữ khi bạn chọn.'
        : 'Dữ liệu trong hệ thống sẽ được cập nhật theo Sheet.',
  };
}

export function toggleSkip(choice: LocalChoice, id: string): LocalChoice {
  const skip = new Set(choice.skip);
  if (!skip.delete(id)) skip.add(id);
  return { ...choice, skip };
}

export function toggleArchive(choice: LocalChoice, naturalKey: string): LocalChoice {
  const archive = new Set(choice.archive);
  if (!archive.delete(naturalKey)) archive.add(naturalKey);
  return { ...choice, archive };
}

/** `undo` là lựa chọn trước khi bỏ qua tất cả — Hoàn tác trả đúng tập cũ; chạm một dòng khác thì UI xoá `undo`. */
export function skipAll(rows: Row[], choice: LocalChoice): { choice: LocalChoice; undo: LocalChoice } {
  const skip = new Set(choice.skip);
  for (const r of rows) if (r.effective === 'write') skip.add(r.id);
  return { choice: { ...choice, skip }, undo: choice };
}

export function applyPayload(rows: Row[]): { changeIds: string[]; archiveKeys: string[] } {
  return {
    changeIds: rows.filter((r) => r.effective === 'write').map((r) => r.id),
    archiveKeys: [...new Set(rows.filter((r) => r.archive).map((r) => r.naturalKey))],
  };
}

/** Dòng tick được: dòng sẽ ghi, dòng cần sửa (để bỏ qua / sửa hàng loạt), và (chỉ khu Dự án) dòng không còn trên Sheet có việc để làm (Lưu trữ / Xoá). */
export const isTickable = (r: Row, area: Area): boolean => (r.effective === 'write' && !writeBlocked(r)) || r.effective === 'fix' || goneActionOf(r, area) !== null;

/** Vì sao ô tick của dòng này tắt (title của ô tick); '' khi tick được. */
export function tickBlockedReason(r: Row, area: Area): string {
  if (isTickable(r, area)) return '';
  if (settingsOnly(r)) return 'Đổi trong Cài đặt';
  if (heldByApp(r)) return 'Đã sửa trong app — giữ nguyên';
  if (r.sourceState === 'checking') return 'Đang kiểm nguồn giờ công…';
  if (r.sourceState === 'failed') return 'Không kiểm được giờ máy — bấm Quét lại';
  if (r.effective === 'conflict') return 'Xung đột — quét lại để thấy bản mới';
  if (r.effective === 'gone') return 'Không còn trên Sheet — giữ nguyên';
  return 'Đã bỏ qua — bấm Hoàn tác để quyết lại';
}

export interface Selection {
  /** Dòng đang hiện mà tick được. */
  tickable: Row[];
  /** Dòng đang hiện, tick được và đang được chọn. */
  picked: Row[];
  all: boolean;
  some: boolean;
}

/** Chỉ tính dòng ĐANG HIỆN (sau mọi bộ lọc): id đã chọn nhưng bị lọc ẩn hoặc không còn tick được thì bị bỏ qua, không bị áp dụng ngầm. */
export function selectionOf(shown: Row[], selected: ReadonlySet<string>, area: Area): Selection {
  const tickable = shown.filter((r) => isTickable(r, area));
  const picked = tickable.filter((r) => selected.has(r.id));
  return { tickable, picked, all: tickable.length > 0 && picked.length === tickable.length, some: picked.length > 0 && picked.length < tickable.length };
}

export function toggleSelected(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (!next.delete(id)) next.add(id);
  return next;
}

/** Ô tick ở header: bật thì chọn mọi dòng tick được đang hiện, tắt thì bỏ chọn chúng (giữ id của dòng bị lọc ẩn). */
export function setAllSelected(selected: ReadonlySet<string>, tickable: Row[], on: boolean): Set<string> {
  const next = new Set(selected);
  for (const r of tickable) {
    if (on) next.add(r.id);
    else next.delete(r.id);
  }
  return next;
}

/** Lưu trữ / Giữ nguyên hàng loạt cho các dòng "không còn trên Sheet" đã chọn (theo natural_key). */
export function setArchivePicked(picked: Row[], choice: LocalChoice, on: boolean): LocalChoice {
  const archive = new Set(choice.archive);
  for (const r of picked) {
    if (r.effective !== 'gone') continue;
    if (on) archive.add(r.naturalKey);
    else archive.delete(r.naturalKey);
  }
  return { ...choice, archive };
}

/** Số liệu cho panel khi có dòng được chọn. `apply` = số thay đổi sẽ ghi nếu bấm Áp dụng. */
export function pickedSummary(picked: Row[]): { total: number; write: number; fix: number; gone: number; archiving: number; apply: number } {
  const write = picked.filter((r) => r.effective === 'write').length;
  const fix = picked.filter((r) => r.effective === 'fix').length;
  const gone = picked.filter((r) => r.effective === 'gone').length;
  const archiving = picked.filter((r) => r.archive).length;
  return { total: picked.length, write, fix, gone, archiving, apply: write + archiving };
}

/** Khoá ổn định của một thay đổi giữa hai lần quét (id đổi mỗi lần quét): vị trí ô VÀ giá trị mới, để lựa chọn không dời sang ô đã bị người khác sửa. */
const stableKey = (c: SyncChange) => [c.entity, c.naturalKey, c.field ?? '', c.cellA1, JSON.stringify(c.newValue ?? null)].join('|');

/** Sau khi quét lại: dời tập id (đã bỏ qua / đã chọn) từ lần cũ sang id tương ứng của lần mới; dòng không còn hoặc đã đổi giá trị thì rơi mất. */
export function remapIds(prev: readonly SyncChange[], next: readonly SyncChange[], ids: ReadonlySet<string>): Set<string> {
  const count = (list: readonly SyncChange[]) => {
    const m = new Map<string, number>();
    for (const c of list) m.set(stableKey(c), (m.get(stableKey(c)) ?? 0) + 1);
    return m;
  };
  const prevCount = count(prev);
  const nextCount = count(next);
  const keyOfOld = new Map(prev.map((c) => [c.id, stableKey(c)]));
  const idOfNew = new Map(next.map((c) => [stableKey(c), c.id]));
  const out = new Set<string>();
  for (const id of ids) {
    const k = keyOfOld.get(id);
    // Khoá trùng ở lần cũ hoặc lần mới (vd. hai đợt thanh toán cùng khoá, dòng không còn trên Sheet không có ô/giá trị để phân biệt) thì không biết dòng nào
    // là dòng nào: bỏ, để người dùng quyết lại. Ghép nhầm một dòng xoá là xoá nhầm bản ghi, không hoàn tác được.
    const nid = k === undefined || prevCount.get(k) !== 1 || nextCount.get(k) !== 1 ? undefined : idOfNew.get(k);
    if (nid !== undefined) out.add(nid);
  }
  return out;
}

/** Phần sai edge nêu giữa dấu “ ” trong câu lỗi (giai đoạn / tên sai); ô nhiều phần cho nhiều dòng cùng giá trị ô nên phải phân biệt bằng nó. */
const quotedPart = (r: Row): string => (r.error ? /“([^”]+)”/.exec(r.error)?.[1] ?? '' : '');

/** Các dòng "Cần sửa" cùng mã lỗi, cùng giá trị sai và cùng phần sai với `row` (kể cả chính nó) — sửa một lần cho cả nhóm. */
export function sameFixGroup(rows: readonly Row[], row: Row): Row[] {
  return rows.filter((r) => r.effective === 'fix' && r.errorCode === row.errorCode && String(r.newValue ?? '') === String(row.newValue ?? '') && quotedPart(r) === quotedPart(row));
}

export interface FacetItem { value: string; label: string; count: number }

const CODE_IN_LABEL = /^([A-Z]{2}-\d{3})(?!\d)/;

/**
 * Bản ghi mà một dòng thuộc về, cho bộ lọc theo bản ghi; cách gom do `SheetTabConfig.recordFilter.group` quyết:
 * 'project-code' gộp dự án + giai đoạn + mốc + đợt về MÃ DỰ ÁN (khoá mốc/đợt là khoá ghép), dòng chưa có mã rơi về khoá của nó;
 * 'label-head' lấy phần đầu nhãn dòng ("XMA-01 · 2026-10-05" → người); 'natural-key' dùng khoá tự nhiên.
 */
export function recordOf(r: SyncChange, group: RecordGroup): { value: string; label: string } {
  if (group === 'natural-key') return { value: r.naturalKey, label: r.rowLabel || r.naturalKey };
  if (group === 'label-head') {
    const head = r.rowLabel.split(' · ')[0].trim();
    return head ? { value: head, label: head } : { value: r.naturalKey, label: r.naturalKey };
  }
  const code = /^[A-Z]{2}-\d{3}$/.test(r.naturalKey) ? r.naturalKey : CODE_IN_LABEL.exec(r.rowLabel)?.[1];
  return code ? { value: code, label: code } : { value: r.naturalKey, label: r.rowLabel || r.naturalKey };
}

/** Tên cột (Trường) của một dòng, đúng chữ hiện ở cột "Trường · ô". */
export const fieldOf = (r: SyncChange): string => fieldLabel(r.entity, r.field) || (r.kind === 'create' ? 'Bản ghi mới' : entityLabel(r.entity));

function count(items: { value: string; label: string }[]): FacetItem[] {
  const m = new Map<string, FacetItem>();
  for (const i of items) {
    const f = m.get(i.value);
    if (f) f.count += 1;
    else m.set(i.value, { ...i, count: 1 });
  }
  return [...m.values()].sort((a, b) => a.label.localeCompare(b.label, 'vi', { numeric: true }));
}

/** Lựa chọn của hai bộ lọc Bản ghi / Trường, dẫn xuất từ chính các dòng của lần này (kèm số dòng). */
export function facetItems(rows: readonly SyncChange[], group: RecordGroup): { records: FacetItem[]; fields: FacetItem[] } {
  return { records: count(rows.map((r) => recordOf(r, group))), fields: count(rows.map((r) => ({ value: fieldOf(r), label: fieldOf(r) }))) };
}

/** Giữ dòng thuộc các bản ghi / trường đã chọn; danh sách rỗng = không lọc trục đó. */
export function applyFacets<T extends SyncChange>(rows: readonly T[], group: RecordGroup, records: readonly string[], fields: readonly string[]): T[] {
  const rec = new Set(records);
  const fld = new Set(fields);
  return rows.filter((r) => (rec.size === 0 || rec.has(recordOf(r, group).value)) && (fld.size === 0 || fld.has(fieldOf(r))));
}

const ACTION_ORDER: readonly Effective[] = ['write', 'fix', 'conflict', 'gone', 'skipped'];
const ACTION_LABEL: Record<Effective, string> = {
  write: 'Cập nhật lại', fix: 'Cần sửa', conflict: 'Xung đột', gone: 'Không còn trên Sheet', skipped: 'Đã bỏ qua',
};
export const isAction = (v: unknown): v is Effective => ACTION_ORDER.includes(v as Effective);

/** Lựa chọn của bộ lọc Hành động: chỉ trạng thái đang có dòng, kèm số dòng, theo thứ tự cố định. */
export function actionItems(rows: readonly Row[]): FacetItem[] {
  return ACTION_ORDER
    .map((k) => ({ value: k, label: ACTION_LABEL[k], count: rows.filter((r) => r.effective === k).length }))
    .filter((i) => i.count > 0);
}

/** Danh sách rỗng = tất cả (không lọc). */
export function applyActions<T extends Row>(rows: readonly T[], actions: readonly Effective[]): T[] {
  return actions.length === 0 ? [...rows] : rows.filter((r) => actions.includes(r.effective));
}
